import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import {
  RegistrationFilterValue,
  RegistrationForm,
  RegistrationListMeta,
  RegistrationRow,
} from "../types";
import { initialRegistrationForm, initialState } from "./initialState";

export const sliceKey = "registrationState";

type RegistrationListSuccessPayload = {
  data: RegistrationRow[];
  meta: RegistrationListMeta;
};

const registrationSlice = createSlice({
  name: sliceKey,
  initialState,
  reducers: {
    getRegistrationList(state) {
      state.loading = true;
      state.error = null;
      state.page = 1;
    },

    getRegistrationListSuccess(
      state,
      action: PayloadAction<RegistrationListSuccessPayload>
    ) {
      state.loading = false;
      state.list = action.payload.data;
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getRegistrationListError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    getNextPageItems(state) {
      if (state.loading || state.meta.currentPage >= state.meta.totalPages) {
        return;
      }
      state.loading = true;
      state.error = null;
      state.page = state.meta.currentPage + 1;
    },

    getNextPageItemsSuccess(
      state,
      action: PayloadAction<RegistrationListSuccessPayload>
    ) {
      state.loading = false;
      state.list = state.list.concat(action.payload.data);
      state.meta = action.payload.meta;
      state.page = action.payload.meta.currentPage;
      state.error = null;
    },

    getNextPageItemsError(state, action: PayloadAction<string>) {
      state.loading = false;
      state.error = action.payload;
    },

    setDriverCabinFilter(
      state,
      action: PayloadAction<RegistrationFilterValue>
    ) {
      state.driverCabinFilter = action.payload;
    },

    setAxlesMeasureFilter(
      state,
      action: PayloadAction<RegistrationFilterValue>
    ) {
      state.axlesMeasureFilter = action.payload;
    },

    setFrontLoadFilter(state, action: PayloadAction<RegistrationFilterValue>) {
      state.frontLoadFilter = action.payload;
    },

    setRearAxlesMeasureFilter(
      state,
      action: PayloadAction<RegistrationFilterValue>
    ) {
      state.rearAxlesMeasureFilter = action.payload;
    },

    setWeightFilter(state, action: PayloadAction<RegistrationFilterValue>) {
      state.weightFilter = action.payload;
    },

    setTireFilter(state, action: PayloadAction<RegistrationFilterValue>) {
      state.tireFilter = action.payload;
    },

    setDhTypeFilter(state, action: PayloadAction<RegistrationFilterValue>) {
      state.dhTypeFilter = action.payload;
    },

    setBodyTypeFilter(state, action: PayloadAction<RegistrationFilterValue>) {
      state.bodyTypeFilter = action.payload;
    },

    openAddModal(state) {
      state.isAddModalOpen = true;
      state.error = null;
    },

    closeAddModal(state) {
      state.isAddModalOpen = false;
      state.saving = false;
      state.error = null;
      state.form = initialRegistrationForm;
    },

    setFormField(
      state,
      action: PayloadAction<{
        name: keyof RegistrationForm;
        value: string;
      }>
    ) {
      const { name, value } = action.payload;
      state.form[name] = value;
    },

    resetForm(state) {
      state.form = initialRegistrationForm;
    },

    addRegistration(state) {
      state.saving = true;
      state.error = null;
    },

    addRegistrationSuccess(state) {
      state.saving = false;
      state.isAddModalOpen = false;
      state.form = initialRegistrationForm;
      state.error = null;
    },

    addRegistrationError(state, action: PayloadAction<string>) {
      state.saving = false;
      state.error = action.payload;
    },
  },
});

export const { actions: registrationActions, reducer } = registrationSlice;
